import React, { useState, useEffect, useRef } from 'react';
import CalendarSideWidget from './CalendarSideWidget';
import DayDetailsContent from './DayDetailsContent';
import AddEventContent from './AddEventContent';
import AddTithiContent from './AddTithiContent';
import './CalendarDayView.css';

/**
 * CalendarDayView
 * Desktop full-page split-pane: mini-calendar on left, day content on right.
 *
 * Props:
 *   isOpen            — boolean
 *   onClose           — () => void
 *   initialPanel      — 'details' | 'addEvent' | 'addTithi'
 *   activeDate        — AD date string "YYYY-MM-DD"
 *   onDateChange      — (newAdDateString) => void
 *   tithis            — array
 *   publicEvents      — array
 *   personalEvents    — array
 *   user              — Firebase user or null
 *   isAdmin           — boolean
 *   isSuperUser       — boolean
 *   permsLoading      — boolean
 *   hasPermission     — function
 *   trees             — array
 *   onDeleteEvent     — function
 *   onTreeEventClick  — function
 *   getTithiDisplayName   — function
 *   formatTithiDateTime   — function
 *   getTreeMemberName     — function
 *   onAddTithi        — function
 *   authLoading       — boolean
 *   findTithisForAdDate   — function
 *   eventsData        — { [adDateKey]: { hasPublic?, hasPrivate?, hasTithi? } }
 */
export default function CalendarDayView({
  isOpen,
  onClose,
  initialPanel = 'details',
  activeDate,
  onDateChange,
  tithis = [],
  publicEvents = [],
  personalEvents = [],
  user,
  isAdmin,
  isSuperUser,
  permsLoading,
  hasPermission,
  trees = [],
  onDeleteEvent,
  onTreeEventClick,
  getTithiDisplayName,
  formatTithiDateTime,
  getTreeMemberName,
  onAddTithi,
  authLoading,
  findTithisForAdDate,
  eventsData = {},
}) {
  const [panel, setPanel] = useState(initialPanel || 'details');
  const [animating, setAnimating] = useState(false);

  // Swipe-to-dismiss (touch devices — phones & tablets)
  const cdvContainerRef = useRef(null);
  const cdvRightRef = useRef(null);
  const dismissTimerRef = useRef(null);
  const onCloseRef = useRef(onClose);
  // Drag state in refs — no re-renders during gesture
  const dragStartY = useRef(0);
  const isDragging = useRef(false);
  const touchStartedAtTopRef = useRef(false);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Non-passive touchmove listener so e.preventDefault() blocks pull-to-refresh
  // and background scroll. React synthetic onTouchMove is passive by default.
  useEffect(() => {
    const panelEl = cdvRightRef.current;
    const container = cdvContainerRef.current;
    if (!panelEl || !container || !isOpen) return;

    const getActiveScrollElement = () => {
      const modalBody = panelEl.querySelector('.nc-modal-body');
      return modalBody || panelEl;
    };

    const handleTouchStart = (e) => {
      dragStartY.current = e.touches[0].clientY;
      isDragging.current = false;

      const scrollEl = getActiveScrollElement();
      const scrollTop = scrollEl?.scrollTop ?? 0;
      // Dismiss is allowed only when the current panel content is already at top.
      touchStartedAtTopRef.current = scrollTop <= 0;
    };

    const handleTouchMove = (e) => {
      if (!touchStartedAtTopRef.current) return;

      const delta = Math.max(0, e.touches[0].clientY - dragStartY.current);
      if (delta <= 0) return;

      if (!isDragging.current) {
        isDragging.current = true;
        container.style.transition = 'none';
      }

      e.preventDefault();
      container.style.transform = `translateY(${delta}px)`;
      container.style.opacity = String(Math.max(0.5, 1 - delta / 300));
    };

    const handleTouchEnd = () => {
      touchStartedAtTopRef.current = false;
      if (!isDragging.current) return;

      isDragging.current = false;
      const m = (container.style.transform || '').match(/translateY\((\d+(?:\.\d+)?)px\)/);
      const delta = m ? parseFloat(m[1]) : 0;
      container.style.transition = 'transform 0.25s ease, opacity 0.25s ease';

      if (delta > 80) {
        // Keep body lock until onClose() flips isOpen to false. Unlocking here
        // can desync layout during the dismiss animation on mobile browsers.
        container.style.transform = 'translateY(100%)';
        container.style.opacity = '0';
        dismissTimerRef.current = setTimeout(() => {
          dismissTimerRef.current = null;
          if (onCloseRef.current) onCloseRef.current();
        }, 260);
      } else {
        container.style.transform = 'translateY(0)';
        container.style.opacity = '1';
      }
    };

    const handleTouchCancel = () => {
      touchStartedAtTopRef.current = false;
      if (!isDragging.current) return;

      isDragging.current = false;
      container.style.transition = 'transform 0.25s ease, opacity 0.25s ease';
      container.style.transform = 'translateY(0)';
      container.style.opacity = '1';
    };

    panelEl.addEventListener('touchstart', handleTouchStart, { passive: true });
    panelEl.addEventListener('touchmove', handleTouchMove, { passive: false });
    panelEl.addEventListener('touchend', handleTouchEnd, { passive: true });
    panelEl.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      panelEl.removeEventListener('touchstart', handleTouchStart);
      panelEl.removeEventListener('touchmove', handleTouchMove);
      panelEl.removeEventListener('touchend', handleTouchEnd);
      panelEl.removeEventListener('touchcancel', handleTouchCancel);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, [isOpen]);

  // Sync panel when initialPanel or activeDate changes from outside
  useEffect(() => {
    setPanel(initialPanel || 'details');
  }, [initialPanel, activeDate]);

  // Scroll position — captured before body lock, restored after unlock
  const scrollYRef = useRef(0);
  const isBodyLockedRef = useRef(false);

  // Lock body scroll while overlay is open; restore scroll position on close
  useEffect(() => {
    if (isOpen) {
      scrollYRef.current = window.scrollY;
      isBodyLockedRef.current = true;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.style.width = '100%';
    } else {
      if (isBodyLockedRef.current) {
        isBodyLockedRef.current = false;
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollYRef.current);
      }
    }
    return () => {
      if (isBodyLockedRef.current) {
        isBodyLockedRef.current = false;
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollYRef.current);
      }
    };
  }, [isOpen]);

  function switchPanel(newPanel) {
    setAnimating(true);
    setTimeout(() => {
      setPanel(newPanel);
      setAnimating(false);
    }, 150);
  }

  function handleSideWidgetDateChange(newDate) {
    onDateChange(newDate);
    setPanel('details');
  }

  function handleOpenAddEvent(adYear, adMonthZeroBased, adDay) {
    const key = `${adYear}-${String(adMonthZeroBased + 1).padStart(2, '0')}-${String(adDay).padStart(2, '0')}`;
    onDateChange(key);
    switchPanel('addEvent');
  }

  function handleOpenAddTithi(adYear, adMonthZeroBased, adDay, focusHint) {
    const key = `${adYear}-${String(adMonthZeroBased + 1).padStart(2, '0')}-${String(adDay).padStart(2, '0')}`;
    onDateChange(key);
    switchPanel('addTithi');
  }

  if (!isOpen) return null;

  return (
    <div
      className="cdv-container"
      ref={cdvContainerRef}
    >
      {/* ── Left: mini calendar (hidden on small screens) ── */}
      <div className="cdv-left">
        <CalendarSideWidget
          selectedDate={activeDate}
          onDateChange={handleSideWidgetDateChange}
          eventsData={eventsData}
        />
      </div>

      <div className="cdv-divider" />

      {/* ── Right: animated panel ───────────────────────────────────────────── */}
      <div
        className={`cdv-right${animating ? ' cdv-animating' : ''}${panel === 'details' ? ' cdv-right--details' : ''}`}
        ref={cdvRightRef}
      >
        {panel === 'details' && (
          <DayDetailsContent
            activeDate={activeDate}
            tithis={tithis}
            publicEvents={publicEvents}
            personalEvents={personalEvents}
            user={user}
            isAdmin={isAdmin}
            isSuperUser={isSuperUser}
            permsLoading={permsLoading}
            hasPermission={hasPermission}
            onOpenAddEvent={handleOpenAddEvent}
            onOpenAddTithi={handleOpenAddTithi}
            onDeleteEvent={onDeleteEvent}
            onTreeEventClick={onTreeEventClick}
            trees={trees}
            getTithiDisplayName={getTithiDisplayName}
            formatTithiDateTime={formatTithiDateTime}
            getTreeMemberName={getTreeMemberName}
            onClose={onClose}
          />
        )}

        {panel === 'addEvent' && (
          <AddEventContent
            isOpen={panel === 'addEvent'}
            onClose={() => switchPanel('details')}
            activeDate={activeDate}
            user={user}
            authLoading={authLoading}
            isAdmin={isAdmin}
            isSuperUser={isSuperUser}
            findTithisForAdDate={findTithisForAdDate}
          />
        )}

        {panel === 'addTithi' && (
          <AddTithiContent
            isOpen={panel === 'addTithi'}
            onClose={() => switchPanel('details')}
            activeDate={activeDate}
            user={user}
            authLoading={authLoading}
            onAddTithi={onAddTithi}
          />
        )}
      </div>
    </div>
  );
}
